import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  HelperDataResponse,
  CategoryResponse,
  BaseUom,
  PurchaseUom,
  Tag,
  ItemType,
  ItemStatus,
  DraftResponse,
} from "@/app/types/items.types";

interface ItemsState {
  helperData: HelperDataResponse | null;
  isHelperDataLoaded: boolean;
  categories: CategoryResponse[] | null;
  isCategoriesLoaded: boolean;
  draft: DraftResponse | null;
  isDraftLoaded: boolean;
  draftId: string | null;
  isItemsLoaded: boolean;
}

const initialState: ItemsState = {
  helperData: null,
  isHelperDataLoaded: false,
  categories: null,
  isCategoriesLoaded: false,
  draft: null,
  isDraftLoaded: false,
  draftId: null,
  isItemsLoaded: false,
};

const itemsSlice = createSlice({
  name: "items",
  initialState,
  reducers: {
    setHelperData: (state, action: PayloadAction<HelperDataResponse>) => {
      state.helperData = action.payload;
      state.isHelperDataLoaded = true;
    },
    clearHelperData: (state) => {
      state.helperData = null;
      state.isHelperDataLoaded = false;
    },
    setCategories: (state, action: PayloadAction<CategoryResponse[]>) => {
      state.categories = action.payload;
      state.isCategoriesLoaded = true;
    },
    clearCategories: (state) => {
      state.categories = null;
      state.isCategoriesLoaded = false;
    },

    setDraft: (state, action: PayloadAction<DraftResponse>) => {
      state.draft = action.payload;
      state.isDraftLoaded = true;
    },
    clearDraft: (state) => {
      state.draft = null;
      state.isDraftLoaded = false;
    },
    setDraftId: (state, action: PayloadAction<string>) => {
      state.draftId = action.payload;
    },

    setItemsLoaded: (state, action: PayloadAction<boolean>) => {
      state.isItemsLoaded = action.payload;
    },

  },
});

export const { setHelperData, clearHelperData, setCategories, clearCategories, setDraft, clearDraft, setDraftId, setItemsLoaded } = itemsSlice.actions;

export const itemsReducer = itemsSlice.reducer;

export const selectHelperData = (state: { items: ItemsState }) =>
  state.items.helperData;

export const selectIsHelperDataLoaded = (state: { items: ItemsState }) =>
  state.items.isHelperDataLoaded;

export const selectCategories = (state: { items: ItemsState }): CategoryResponse[] =>
  state.items.categories ?? [];

export const DraftData = (state: { items: ItemsState }): DraftResponse | null =>
  state.items.draft;
export const selectIsDraftLoaded = (state: { items: ItemsState }) =>
  state.items.isDraftLoaded;
export const selectIsCategoriesLoaded = (state: { items: ItemsState }) =>
  state.items.isCategoriesLoaded;
export const selectBaseUOMs = (state: { items: ItemsState }): BaseUom[] =>
  state.items.helperData?.baseUOM ?? [];

export const selectPurchaseUOMs = (state: { items: ItemsState }): PurchaseUom[] =>
  state.items.helperData?.purchaseUOM ?? [];

export const selectTags = (state: { items: ItemsState }): Tag[] =>
  state.items.helperData?.tags ?? [];

export const selectItemTypes = (state: { items: ItemsState }): ItemType[] =>
  state.items.helperData?.item_types ?? [];

export const selectItemStatus = (state: { items: ItemsState }): ItemStatus | null =>
  state.items.helperData?.item_status ?? null;
export const selectDraftId = (state: { items: ItemsState }): string | null =>
  state.items.draftId;
export const selectIsItemsLoaded = (state: { items: ItemsState }) =>
  state.items.isItemsLoaded;